import React, { useEffect, useState } from "react";
import { goToCheckout } from "@/lib/checkout";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { COUNTRIES } from "@/lib/countries";
import { useLang } from "@/context/LanguageContext";
import { formatMoney } from "@/lib/money";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Truck } from "lucide-react";
import { FindoSpinner } from "@/components/FindoSpinner";

// Small confirmation modal shown BEFORE Stripe: the client reviews / edits the
// shipping address (pre-filled from their profile + declaration) then pays.
export function ShippingPayModal({ declarationId, open, onClose }) {
  const { t, lang } = useLang();
  const [decl, setDecl] = useState(null);
  const [estimate, setEstimate] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    name: "", phone: "", address_line1: "", address_line2: "", postal_code: "", city: "", country: "FR",
  });

  useEffect(() => {
    if (!open || !declarationId) return;
    setDecl(null);
    api(`/declarations/${declarationId}/public`, { auth: false }).then((d) => {
      setDecl(d);
      const a = d.shipping_address || {};
      api("/b2c/me", { b2c: true }).then((me) => {
        setForm({
          name: (a.name) || me?.name || d.customer_name || "",
          phone: a.phone || me?.phone || d.customer_phone || "",
          address_line1: a.address_line1 || "",
          address_line2: a.address_line2 || "",
          postal_code: a.postal_code || me?.postal_code || "",
          city: a.city || me?.city || "",
          country: a.country || me?.country || "FR",
        });
      }).catch(() => {
        setForm({
          name: a.name || d.customer_name || "",
          phone: a.phone || d.customer_phone || "",
          address_line1: a.address_line1 || "", address_line2: a.address_line2 || "",
          postal_code: a.postal_code || "", city: a.city || "", country: a.country || "FR",
        });
      });
    }).catch(() => toast.error(t("hc.declarationNotFound2")));
    api(`/declarations/${declarationId}/shipping-estimate`, { auth: false })
      .then((e) => { if (e && e.available) setEstimate(e); }).catch(() => {});
  }, [open, declarationId]);

  const shipPrice = (estimate && estimate.price_eur != null)
    ? estimate.price_eur
    : Number(decl?.item_preview?.shipping_price_eur || 0);

  async function payNow() {
    if (!form.address_line1 || !form.postal_code || !form.city) {
      toast.error(t("hc.pleaseCompleteYourShippingAddress"));
      return;
    }
    if (!form.phone || !form.phone.trim()) {
      toast.error(t("hc.pleaseProvideAPhoneNumber"));
      return;
    }
    // Plausible-phone check for instant feedback; the backend is the authoritative
    // gate: it normalises to an international E.164 number (+indicatif) using the
    // destination country, and rejects anything it cannot make international.
    const cleanedPhone = form.phone.replace(/[\s().\-/]/g, "");
    if (!/^\+?\d{6,15}$/.test(cleanedPhone)) {
      toast.error(t("hc.invalidNumberEnterAnInternational"));
      return;
    }
    setBusy(true);
    try {
      const res = await api(`/declarations/${declarationId}/shipping`, {
        method: "POST", auth: false,
        body: {
          item_id: decl.item_preview.id,
          recipient_name: form.name,
          customer_phone: form.phone,
          address_line1: form.address_line1,
          address_line2: form.address_line2,
          postal_code: form.postal_code,
          city: form.city,
          country: form.country,
          weight_kg: 0,
          origin_url: window.location.origin,
        },
      });
      if (res.already_paid) {
        toast.success(t("hc.yourShippingFeesHaveAlready"));
        onClose?.();
        setTimeout(() => window.location.reload(), 600);
        return;
      }
      if (res.checkout_url) { goToCheckout(res.checkout_url); return; }
      toast.error(t("hc.unableToOpenTheShipping"));
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="sm:max-w-md" data-testid="ship-pay-modal" onCloseAutoFocus={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="font-display text-lg text-findo-navy flex items-center gap-2">
            <Truck size={18} className="text-findo-navy" /> {t("hc.shippingAddress")}
          </DialogTitle>
        </DialogHeader>
        {!decl ? (
          <div className="py-10 grid place-items-center text-slate-400"><FindoSpinner size={22} /></div>
        ) : (
          <>
            <p className="text-sm text-slate-500 -mt-1">{t("hc.checkOrEditYourDetails")}</p>
            <div className="space-y-2.5 mt-1 max-h-[56vh] overflow-y-auto pr-1">
              <div><Label className="text-sm">{t("hc.fullName")}</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 h-10 rounded-xl" data-testid="ship-modal-name" /></div>
              <div><Label className="text-sm">{t("hc.phoneNumber")} <span className="text-findo-red">*</span></Label><Input type="tel" required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+33 6 12 34 56 78" className="mt-1 h-10 rounded-xl" data-testid="ship-modal-phone" /></div>
              <div><Label className="text-sm">{t("ship.addr1")}</Label><Input value={form.address_line1} onChange={(e) => setForm({ ...form, address_line1: e.target.value })} className="mt-1 h-10 rounded-xl" data-testid="ship-modal-addr1" /></div>
              <div><Label className="text-sm">{t("ship.addr2")}</Label><Input value={form.address_line2} onChange={(e) => setForm({ ...form, address_line2: e.target.value })} className="mt-1 h-10 rounded-xl" data-testid="ship-modal-addr2" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-sm">{t("ship.postal")}</Label><Input value={form.postal_code} onChange={(e) => setForm({ ...form, postal_code: e.target.value })} className="mt-1 h-10 rounded-xl" data-testid="ship-modal-postal" /></div>
                <div><Label className="text-sm">{t("ship.city")}</Label><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="mt-1 h-10 rounded-xl" data-testid="ship-modal-city" /></div>
              </div>
              <div>
                <Label className="text-sm">{t("ship.country")}</Label>
                <select value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} className="mt-1 h-10 w-full rounded-full border border-border px-3 text-sm" data-testid="ship-modal-country">
                  {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.flag} {c.name}</option>)}
                </select>
              </div>
            </div>
            <DialogFooter className="mt-3">
              <Button variant="ghost" onClick={onClose} disabled={busy} className="rounded-full font-semibold text-slate-500">{t("hc.cancel")}</Button>
              <Button onClick={payNow} disabled={busy} className="rounded-full font-semibold bg-findo-navy hover:bg-findo-navy/90" data-testid="ship-modal-pay-btn">
                {busy ? <><FindoSpinner size={16} className="mr-1.5" /> {t("ship.submitting")}</> : `Valider et payer · ${formatMoney(shipPrice, lang)}`}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
